import { ArrowRight, Check, ShieldCheck, MapPin, ClipboardList, LayoutGrid, HeartHandshake, Compass } from "lucide-react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { useDocumentMeta } from "@/lib/seo";

const PETROLE = "#284E55";

const INCLUDED = [
  "Espace Partenaire : prospects, clients, projets et petits travaux",
  "Méthode CozyHome et outils de suivi de chantier",
  "Accès aux entreprises et artisans du réseau",
  "Catalogue des services techniques CozyHome Central à la commande",
  "Outils de communication et ressources professionnelles",
  "Secteur attribué après validation de votre dossier",
];

const SEPARATE = ["Plans", "Déclaration préalable", "Permis de construire", "Visuels 3D", "Packs techniques", "Autres prestations techniques"];

const STEPS = [
  ["Candidature", "Vous présentez votre profil et le secteur sur lequel vous souhaitez développer votre activité."],
  ["Dossier professionnel", "Les justificatifs nécessaires sont demandés après une première étude de votre profil."],
  ["Validation CozyHome", "Nos équipes examinent votre dossier et vérifient à nouveau la disponibilité du secteur."],
  ["Souscription", "Vous choisissez votre secteur (249 € HT/mois, 179 € le 2e, 99 € à partir du 3e)."],
  ["Espace Partenaire", "Votre espace professionnel s'ouvre — le secteur reste en attente d'activation."],
  ["Activation du territoire", "CozyHome active votre secteur dès que le dossier est entièrement conforme."],
  ["Accès complet", "Toutes les fonctions de l'Espace Partenaire sont ouvertes, les projets commencent à arriver."],
];

const PILLARS = [
  { icon: LayoutGrid, title: "Simple à prendre en main", text: "Un espace conçu pour organiser prospects, clients et projets sans multiplier les outils." },
  { icon: HeartHandshake, title: "Vous n'êtes pas seul", text: "Méthode CozyHome, services techniques, outils, communication et écosystème professionnel." },
  { icon: Compass, title: "Vous restez indépendant", text: "Vous développez votre propre activité de maître d'œuvre au sein de l'écosystème CozyHome." },
];

const CtaButton = ({ testid, className = "" }) => (
  <Link
    to="/nous-rejoindre/partenaire"
    data-testid={testid}
    className={`group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-sm font-bold text-white transition-opacity duration-300 hover:opacity-90 ${className}`}
    style={{ backgroundColor: PETROLE }}
  >
    Déposer ma candidature
    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
  </Link>
);

export default function DevenirPartenairePage() {
  useDocumentMeta(
    "Devenir Partenaire CozyHome — L'offre Partenaire à 249 € HT / mois | CozyHome",
    "Développez votre activité de maîtrise d'œuvre avec CozyHome : espace Partenaire, méthode, services techniques et écosystème professionnel. Secteur attribué après validation."
  );

  return (
    <div className="App">
      <Navbar />
      <PageHero
        testid="page-devenir-partenaire-hero"
        label="Devenir Partenaire CozyHome"
        title={<>Concentrez-vous sur le client.<br /><span className="italic" style={{ color: PETROLE }}>CozyHome simplifie le reste.</span></>}
        sub="Développez votre activité de maîtrise d'œuvre sans avoir à tout construire seul. CozyHome vous apporte une méthode, des outils et un écosystème pour simplifier votre quotidien."
      />

      <section className="bg-ivory pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl space-y-10 px-5 sm:px-8">
          {/* Offre + prix */}
          <Reveal>
            <article data-testid="devenir-offre" className="overflow-hidden rounded-3xl bg-white">
              <div className="h-1.5 w-full" style={{ backgroundColor: PETROLE }} aria-hidden="true" />
              <div className="grid gap-10 p-7 sm:p-12 lg:grid-cols-5 lg:gap-14">
                <div className="lg:col-span-3">
                  <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: PETROLE }}>L'offre CozyHome Partenaire</p>
                  <h2 className="mt-4 font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Ce qui est inclus</h2>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-500 sm:text-base">
                    Vous vous concentrez sur l'essentiel : la relation client, le développement commercial et le
                    pilotage local des projets. CozyHome vous accompagne avec les outils de gestion, les services
                    techniques disponibles à la commande, les ressources professionnelles et un environnement conçu
                    pour réduire les tâches chronophages.
                  </p>
                  <ul data-testid="devenir-inclus" className="mt-7 space-y-3.5">
                    {INCLUDED.map((f) => (
                      <li key={f} className="flex gap-3 text-sm leading-relaxed text-stone-600 sm:text-base">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full" style={{ backgroundColor: `${PETROLE}1A`, color: PETROLE }}>
                          <Check className="h-3 w-3" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="lg:col-span-2">
                  <div className="flex h-full flex-col rounded-2xl p-7 sm:p-8" style={{ backgroundColor: `${PETROLE}0D` }}>
                    <p className="text-xs font-bold uppercase tracking-[0.22em]" style={{ color: PETROLE }}>Abonnement Partenaire</p>
                    <p data-testid="devenir-price" className="mt-4 font-display text-4xl font-medium tracking-tight text-navy sm:text-5xl">
                      249 € <span className="text-xl text-stone-500 sm:text-2xl">HT / mois</span>
                    </p>
                    <p className="mt-4 text-xs leading-relaxed text-stone-500">
                      Les prestations techniques commandées auprès de CozyHome Central sont facturées séparément.
                    </p>
                    <p className="mt-6 font-display text-lg italic leading-snug text-navy sm:text-xl">
                      « Moins de tracas. Plus de temps pour vos clients et vos projets. »
                    </p>
                    <div className="mt-auto pt-8">
                      <CtaButton testid="devenir-cta-principal" />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </Reveal>

          {/* 3 piliers */}
          <Reveal>
            <div data-testid="devenir-piliers" className="grid gap-4 md:grid-cols-3">
              {PILLARS.map((b) => (
                <div key={b.title} className="rounded-3xl bg-white p-7">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: `${PETROLE}14`, color: PETROLE }}>
                    <b.icon className="h-4.5 w-4.5" />
                  </span>
                  <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-navy">{b.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-stone-500">{b.text}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Services séparés + secteur */}
          <Reveal>
            <div className="grid gap-4 lg:grid-cols-2">
              <article data-testid="devenir-services" className="rounded-3xl bg-white p-7 sm:p-10">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cognac/10 text-cognac-dark"><ClipboardList className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-2xl font-medium text-navy sm:text-3xl">Les services techniques, facturés séparément</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-500 sm:text-base">
                  Vous les commandez à CozyHome Central, projet par projet, uniquement quand vous en avez besoin.
                  Ils ne sont pas inclus dans l'abonnement.
                </p>
                <ul className="mt-5 flex flex-wrap gap-2">
                  {SEPARATE.map((s) => (
                    <li key={s} className="rounded-full border border-[#E4DFD5] bg-cream px-3.5 py-2 text-xs font-semibold text-navy">{s}</li>
                  ))}
                </ul>
              </article>
              <article data-testid="devenir-secteur" className="rounded-3xl bg-white p-7 sm:p-10">
                <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ backgroundColor: `${PETROLE}14`, color: PETROLE }}><MapPin className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-2xl font-medium text-navy sm:text-3xl">Un secteur attribué après validation</h3>
                <p className="mt-3 text-sm leading-relaxed text-stone-500 sm:text-base">
                  Chaque Partenaire CozyHome développe son activité sur un secteur qui lui est attribué par CozyHome
                  une fois son dossier professionnel vérifié et validé. Le secteur, le code partenaire et le statut
                  de conformité sont gérés par CozyHome Central.
                </p>
                <p className="mt-4 rounded-2xl bg-cream p-4 text-xs leading-relaxed text-stone-600">
                  La souscription à l'abonnement ne vaut pas validation automatique : l'accès complet à l'Espace
                  Partenaire et l'attribution d'un secteur interviennent après vérification du dossier par CozyHome.
                </p>
              </article>
            </div>
          </Reveal>

          {/* Intégration */}
          <Reveal>
            <article data-testid="devenir-integration" className="rounded-3xl p-7 text-white sm:p-12" style={{ backgroundColor: PETROLE }}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-cognac-light">Comment se passe l'intégration</p>
                  <h2 className="mt-4 font-display text-3xl font-medium tracking-tight sm:text-4xl">Sept étapes, une seule promesse : un réseau de confiance.</h2>
                </div>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold"><ShieldCheck className="h-4 w-4 text-cognac-light" /> Dossier vérifié par CozyHome</span>
              </div>
              <ol className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {STEPS.map(([t, d], i) => (
                  <li key={t} className="rounded-2xl bg-white/10 p-5">
                    <span className="font-display text-2xl font-medium text-cognac-light">{String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-2 text-sm font-bold">{t}</p>
                    <p className="mt-1 text-xs leading-relaxed text-white/70">{d}</p>
                  </li>
                ))}
                <li className="flex flex-col justify-between rounded-2xl border border-dashed border-white/25 p-5">
                  <p className="text-sm leading-relaxed text-white/85">
                    Vous avez le sens du commerce, de l'organisation et de la relation client ?
                  </p>
                  <Link
                    to="/nous-rejoindre/partenaire"
                    data-testid="devenir-cta-secondaire"
                    className="group mt-4 inline-flex items-center gap-2 self-start rounded-full bg-white px-5 py-2.5 text-xs font-bold transition-colors hover:bg-cream"
                    style={{ color: PETROLE }}
                  >
                    Déposer ma candidature <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              </ol>
            </article>
          </Reveal>

          {/* CTA final */}
          <Reveal>
            <div data-testid="devenir-cta-final" className="flex flex-col items-start gap-6 rounded-3xl bg-white p-7 sm:flex-row sm:items-center sm:justify-between sm:p-12">
              <div>
                <h2 className="font-display text-3xl font-medium tracking-tight text-navy sm:text-4xl">Prêt à développer votre activité avec CozyHome ?</h2>
                <p className="mt-3 max-w-xl text-sm leading-relaxed text-stone-500 sm:text-base">
                  Candidature, dossier professionnel, souscription : votre parcours Partenaire commence par une
                  candidature simple, sans document ni paiement à cette étape.
                </p>
              </div>
              <CtaButton testid="devenir-cta-final-btn" className="shrink-0" />
            </div>
          </Reveal>
        </div>
      </section>
      <Footer />
    </div>
  );
}
